/* Birthday Automation — frontend data access.
 *
 * Reads happen via Supabase REST under RLS (admin/hr only — the backend already
 * protects this data). Backend-function calls are: the read-only parallel
 * comparison, and the gated approval action `run-action` (Phase 10.2 — state
 * transitions only). There is NO sending here: no n8n, no email, no force-send,
 * no "send approved now", no storage/photo writes. The backend state machine
 * (api/_modules/birthday/stateMachine.js) is the single authority and
 * re-validates every transition; this layer only initiates the request.
 *
 * Mirrors the existing Nexus separate-tab convention (own SB_URL + sbH helpers).
 * Any date math here is display-only; the backend remains the authority.
 */

const SB_URL =
  process.env.REACT_APP_SUPABASE_URL ||
  "https://gufmotkoeoqvmxjeqbmw.supabase.co";

const sbH = (key, token) => ({
  apikey: key,
  Authorization: `Bearer ${token || key}`,
  "Content-Type": "application/json",
});

/** Africa/Cairo "today" for display/filtering (backend is authoritative). */
export function cairoToday(now = new Date()) {
  const fmt = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Africa/Cairo", year: "numeric", month: "2-digit", day: "2-digit",
  });
  const parts = fmt.formatToParts(now);
  const get = (t) => parts.find((p) => p.type === t).value;
  const y = get("year"), m = get("month"), d = get("day");
  return { iso: `${y}-${m}-${d}`, monthDay: `${m}-${d}`, year: Number(y) };
}

export function createBirthdayApi(anonKey, token) {
  async function sbSelect(table, query) {
    const r = await fetch(`${SB_URL}/rest/v1/${table}?${query}`, { headers: sbH(anonKey, token) });
    if (!r.ok) throw new Error(`${table}: ${r.status} ${await r.text()}`);
    return r.json();
  }

  return {
    /** birthday.* settings as a { key: value } map. */
    async getSettings() {
      const rows = await sbSelect("nexus_settings", "key=like.birthday.*&select=key,value");
      const map = {};
      for (const row of rows) map[row.key] = row.value;
      return map;
    },

    /** Active routing rules. */
    getRoutingRules() {
      return sbSelect("birthday_routing_rules", "select=*&order=priority.asc");
    },

    /** Templates. */
    getTemplates() {
      return sbSelect("birthday_templates", "select=id,name,version,is_active,is_default&order=name.asc");
    },

    /** Count of dry-run birthday jobs (outbox). */
    async getDryRunJobCount() {
      const rows = await sbSelect("nexus_jobs", "module=eq.birthday&status=eq.dry_run&select=id");
      return rows.length;
    },

    /**
     * Today's queue: active employees with a birthday today (Africa/Cairo),
     * each merged with its most recent birthday_run for the year (if any).
     */
    async getTodayQueue() {
      const { monthDay, year } = cairoToday();
      const employees = await sbSelect(
        "employees",
        `is_active=eq.true&birthday_month_day=eq.${encodeURIComponent(monthDay)}` +
          "&select=id,employee_id,name,department,birthday_date,birthday_month_day&order=name.asc"
      );
      let runs = [];
      if (employees.length > 0) {
        const ids = employees.map((e) => e.id).join(",");
        runs = await sbSelect(
          "birthday_runs",
          `employee_id=in.(${ids})&year=eq.${year}` +
            "&select=id,employee_id,status,fingerprint,photo_version_id,template_id,template_version," +
            "previous_employee_year_sent,duplicate_of_run_id,created_at,updated_at&order=created_at.desc"
        );
      }
      const runByEmp = {};
      for (const r of runs) if (!runByEmp[r.employee_id]) runByEmp[r.employee_id] = r; // most recent
      return {
        date: cairoToday().iso,
        rows: employees.map((e) => {
          const run = runByEmp[e.id] || null;
          return { ...e, run, status: run ? run.status : "not_synced" };
        }),
      };
    },

    /** Event timeline for a run (lazy, on expand). */
    getRunEvents(runId) {
      return sbSelect(
        "birthday_events",
        `run_id=eq.${runId}&select=event_type,previous_status,next_status,source,actor_email,created_at&order=created_at.desc&limit=20`
      );
    },

    /** Outbox job(s) for a run (lazy, on expand). */
    getRunJobs(runId) {
      return sbSelect(
        "nexus_jobs",
        `ref_id=eq.${runId}&select=id,job_type,status,created_at&order=created_at.desc&limit=10`
      );
    },

    /**
     * Gated approval/state action (backend function). State transitions ONLY —
     * never sends, never calls n8n, never writes storage. The backend re-checks
     * permissions (admin/hr) and the state machine; this is initiation only.
     *
     * @param {{ runId:string, action:string, reason?:string, idempotencyKey:string }} p
     * @returns {Promise<{run_id,action,previous_status,status,changed,...}>}
     */
    async runAction({ runId, action, reason, idempotencyKey }) {
      const r = await fetch("/api/birthday/run-action", {
        method: "POST",
        headers: { ...sbH(anonKey, token), "Idempotency-Key": idempotencyKey },
        body: JSON.stringify({
          run_id: runId,
          action,
          reason: reason || undefined,
          idempotency_key: idempotencyKey,
        }),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok || body.ok === false) {
        const err = new Error(body?.error?.message || `Action failed (${r.status})`);
        err.code = body?.error?.code || null;
        err.status = r.status;
        err.details = body?.error?.details || null;
        throw err;
      }
      return body.data || body;
    },

    /** Read-only parallel comparison (backend function). */
    async parallelCompare({ date, legacyRows, columnMapping }) {
      const r = await fetch("/api/birthday/parallel-compare", {
        method: "POST",
        headers: sbH(anonKey, token),
        body: JSON.stringify({ date, legacy_rows: legacyRows, column_mapping: columnMapping || {}, dry_run: true }),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok || body.ok === false) {
        throw new Error(body?.error?.message || `Comparison failed (${r.status})`);
      }
      return body.data || body;
    },
  };
}
